/*!
 * CUDIS landing-page tracker — engagement half.
 *
 * Loads on the 5 landers (both the cudis.xyz Shopify pages and their Netlify
 * twins) and reports: pageview, scroll depth, every click, active-time
 * heartbeats and a final exit summary.
 *
 * The commerce half (ATC / initiate-checkout / purchase) is NOT here — those
 * happen on product, cart and checkout pages this script never runs on. They
 * come from the Shopify custom web pixel, which reads the `cudis_lp` cookie
 * this file writes. That cookie is the whole attribution chain: without it a
 * purchase cannot be tied back to the lander that produced it.
 *
 * Endpoint expects text/plain so sendBeacon skips the CORS preflight.
 */
(function () {
  'use strict';
  if (window.__cudisTrack) return;          // never double-instrument
  // Never track inside an iframe. The review site's /queue/ page embeds the
  // Netlify twins as thumbnails, so without this every visit to the queue
  // logged five phantom pageviews.
  try { if (window.top !== window.self) return; } catch (e) { return; }
  window.__cudisTrack = true;

  var ENDPOINT = 'https://cudis-mof-review.netlify.app/.netlify/functions/cudis-track';
  var COOKIE = 'cudis_lp';
  var SESSION_MINS = 30;
  var ATTR_DAYS = 30;

  // ── identity ────────────────────────────────────────────────────────────
  function rid() {
    try {
      return (crypto.randomUUID && crypto.randomUUID().slice(0, 18)) ||
        Math.random().toString(36).slice(2) + Date.now().toString(36);
    } catch (e) { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
  }
  function ls(k, v) {
    try { if (v === undefined) return localStorage.getItem(k); localStorage.setItem(k, v); }
    catch (e) { return null; }
  }

  var vid = ls('cudis_vid');
  if (!vid) { vid = rid(); ls('cudis_vid', vid); }

  // A session is 30 minutes of inactivity, matched to GA4 so the two roughly
  // agree. Stored in localStorage (not sessionStorage) so it survives the hop
  // from the lander to the product page to checkout.
  var now = Date.now();
  var last = +(ls('cudis_sid_ts') || 0);
  var sid = ls('cudis_sid');
  if (!sid || !last || (now - last) > SESSION_MINS * 60000) { sid = rid(); ls('cudis_sid', sid); }
  ls('cudis_sid_ts', String(now));

  // ── which lander is this ────────────────────────────────────────────────
  var path = location.pathname;
  var SLUGS = {
    'lp-broad': 'broad', 'lp-gym': 'gym', 'lp-subscription-cost': 'subcost',
    'lp-quiz': 'quiz', 'lp-owners': 'owners',
    'broad': 'broad', 'gym': 'gym', 'subscription-cost': 'subcost',
    'quiz': 'quiz', 'owners': 'owners'
  };
  var seg = path.replace(/\/+$/, '').split('/').pop();
  var lp = (document.documentElement.getAttribute('data-cudis-lp')) || SLUGS[seg] || null;
  if (!lp) return;                          // not a lander — do nothing at all
  var site = /cudis\.xyz$/i.test(location.hostname) ? 'shopify' : 'netlify';

  // ── button rescue ───────────────────────────────────────────────────────
  // Every CTA renders with a transparent background on the live store: the
  // `background:var(--accent)` in cudis-lp-v2.css loses to an !important
  // declaration from a stylesheet we do not control (a Shopify app injects it;
  // it is CORS-opaque so it cannot be read from the page). The rest of the .btn
  // rule still applies, which is why the buttons keep their padding, radius and
  // white text — they just have no fill. Result: invisible on the white sticky
  // bar, bare underlined text on the dark hero.
  //
  // Re-asserted here with matching weight. This belongs in a stylesheet, not in
  // the tracker — there is a standalone cudis-lp-fix.css to <link> from the
  // theme, and once that is in place this block is simply redundant. It stays
  // as the belt-and-braces copy so the CTAs are never invisible again.
  // The theme header sits OUTSIDE the .cudis-lp wrapper, so it cannot be
  // reached with a descendant selector. Mark the document instead — this only
  // runs on the five landers, so the class is the scope.
  try { document.documentElement.classList.add('cudis-lp-page'); } catch (e) {}

  try {
    var css = document.createElement('style');
    css.id = 'cudis-btn-fix';
    css.textContent =
      '.cudis-lp .btn{background:var(--accent)!important;color:#fff!important}' +
      '.cudis-lp .btn.ghost{background:#fff!important;color:var(--ink)!important}' +
      '.cudis-lp .btn.dark{background:var(--ink)!important;color:#fff!important}' +
      // Day 1 of Round 1: the theme nav out-clicked the offer CTA ~6:1
      // (site navigation 29, "002 Sporty" 20, CTA 8 across 208 ad sessions).
      // Paid traffic arrives for one offer; the nav is just a priced exit.
      // Cart and logo stay — a buyer still needs to reach checkout.
      '.cudis-lp-page .header__menu,' +
      '.cudis-lp-page .menu-drawer-button,' +
      '.cudis-lp-page menu-drawer{display:none!important}';
    (document.head || document.documentElement).appendChild(css);
  } catch (e) {}

  // ── CTA destination ─────────────────────────────────────────────────────
  // The page bodies were authored against /products/cudis-002-sporty-ring, but
  // the offer PDP is the longer handle. Both are the same $399 product (they
  // are duplicate active products), so this changes which page the buyer lands
  // on, not the price.
  //
  // Done here rather than in the page bodies because Shopify's pageUpdate
  // replaces `body` wholesale — there is no partial patch — so correcting a
  // link would mean re-sending five 10KB HTML bodies onto live pages. Query
  // strings are preserved so the UTMs survive the hop. If this script is ever
  // blocked the links still resolve to the same product at the same price, so
  // the failure mode is the old behaviour, not a broken CTA.
  var OFFER_PATH = '/products/cudis-002-sporty-ring-smart-fitness-ring-for-training-recovery';
  function repointCTAs() {
    try {
      var scope = document.querySelector('.cudis-lp') || document;
      var links = scope.querySelectorAll('a[href*="/products/cudis-002-sporty-ring"]');
      for (var i = 0; i < links.length; i++) {
        var raw = links[i].getAttribute('href');
        if (!raw) continue;
        var u;
        try { u = new URL(raw, location.origin); } catch (e) { continue; }
        if (u.pathname === OFFER_PATH) continue;      // already correct
        u.pathname = OFFER_PATH;
        links[i].setAttribute('href', u.toString());
      }
    } catch (e) {}
  }
  repointCTAs();
  // The sticky bar and quiz result CTAs are injected by the page's own scripts,
  // which may run after this one — sweep once more when the DOM settles.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repointCTAs);
  } else {
    setTimeout(repointCTAs, 1200);
  }

  // ── strip internal jargon ───────────────────────────────────────────────
  // "paid-social" is how we describe the channel internally; it means nothing
  // to a buyer and telegraphs that the price is a test. It appears 13 times
  // across the 5 landers in four shapes: the announce bar, the offer band, two
  // FAQ answers, and the legal footer line.
  //
  // Rewritten in the DOM for the same reason the CTA fix lives here — Shopify's
  // pageUpdate replaces `body` wholesale, so a copy change would mean re-sending
  // five full HTML bodies onto live pages. Specific phrasings run first so each
  // sentence still reads naturally; the loose pattern is only a backstop.
  //
  // Replacements are functions, not strings: a literal "$299" in a replacement
  // would be read as the capture-group reference $2 followed by "99".
  var JARGON = [
    [/PAID-SOCIAL OFFER/g,                     function () { return 'LIMITED-TIME OFFER'; }],
    [/\$299 paid[-\s]?social offer, limited time/gi, function () { return '$299 limited-time offer'; }],
    [/Paid[-\s]?social offer, limited time\./g, function () { return 'Limited-time offer.'; }],
    [/a limited paid[-\s]?social offer/gi,     function () { return 'a limited-time offer'; }],
    [/a paid[-\s]?social offer/gi,             function () { return 'a limited-time offer'; }],
    [/paid[-\s]?social/gi,                     function () { return 'limited-time'; }]
  ];
  function stripJargon() {
    try {
      var root = document.querySelector('.cudis-lp');
      if (!root) return;
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: function (n) {
          var p = n.parentNode && n.parentNode.nodeName;
          if (p === 'SCRIPT' || p === 'STYLE') return NodeFilter.FILTER_REJECT;
          return /paid[-\s]?social/i.test(n.nodeValue)
            ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        }
      });
      var nodes = [], n;
      while ((n = walker.nextNode())) nodes.push(n);
      for (var i = 0; i < nodes.length; i++) {
        var v = nodes[i].nodeValue;
        for (var j = 0; j < JARGON.length; j++) v = v.replace(JARGON[j][0], JARGON[j][1]);
        nodes[i].nodeValue = v;
      }
    } catch (e) {}
  }

  // ── offer highlight ─────────────────────────────────────────────────────
  // broad / gym / subscription-cost all anchor the price as $299 struck through
  // from $399. quiz and owners were authored without it — they mention $299 but
  // never show what it is down from, so the "$100 off" in the ad copy has
  // nothing to land against. Add the same two blocks the other three use.
  //
  // The .price-line and .offer-band styles already exist for both scopes in
  // cudis-lp-v2.css (4 and 3 rules each), so this is markup only — no new CSS,
  // and it renders identically to the pages that shipped with it.
  var OFFER_PAGES = { quiz: 1, owners: 1 };
  function addOfferHighlight() {
    try {
      if (!OFFER_PAGES[lp]) return;
      var root = document.querySelector('.cudis-lp');
      if (!root || root.querySelector('.price-line, .offer-band')) return;  // idempotent

      var band = document.createElement('div');
      band.className = 'offer-band';
      band.innerHTML = '<span class="g">🎁</span><div>$299 today — $100 off plus a free extra ' +
                       'band colour. Limited-time offer.</div>';

      var price = document.createElement('div');
      price.className = 'price-line';
      price.innerHTML = '<span class="now">$299</span><span class="was">$399</span>';

      // The other three landers put the band under the hero CTA, but neither of
      // these two has an anchor CTA in the hero — quiz opens on the quiz
      // question itself, owners on the review wall. Dropping a price band in
      // there would cut across the thing the page opens with. Both blocks go to
      // the closing CTA instead, which is the actual conversion moment.
      var fin = root.querySelector('section.final');
      var finCta = fin && fin.querySelector('a.btn');
      if (finCta && finCta.parentNode) {
        finCta.parentNode.insertBefore(band, finCta);
        finCta.parentNode.insertBefore(price, finCta);
      }

      // If a hero anchor CTA does exist (in case these pages change), keep the
      // original placement and put the band there instead.
      var hero = root.querySelector('section.hero');
      var heroCta = hero && hero.querySelector('a.btn');
      if (heroCta && heroCta.parentNode && band.parentNode) {
        heroCta.parentNode.insertBefore(band, heroCta.nextSibling);
      }
    } catch (e) {}
  }
  addOfferHighlight();
  stripJargon();          // after the band is inserted, so it is swept too
  // The quiz swaps in result copy after the user answers; re-sweep then.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stripJargon);
  } else {
    setTimeout(stripJargon, 1500);
  }

  // ── ad attribution ──────────────────────────────────────────────────────
  var qs = new URLSearchParams(location.search);
  var src = qs.get('utm_source'), cmp = qs.get('utm_campaign'), ad = qs.get('utm_content');
  var device = /Mobi|Android|iPhone/i.test(navigator.userAgent) ? 'm'
             : (/iPad|Tablet/i.test(navigator.userAgent) ? 't' : 'd');

  // Hand the attribution to the web pixel. Cookie, not localStorage: the pixel
  // sandbox can read cookies via its browser API but has no access to this
  // page's storage. Domain is left host-only on the Netlify twins (no .xyz).
  try {
    var payload = encodeURIComponent(JSON.stringify({
      lp: lp, sid: sid, vid: vid, src: src, cmp: cmp, ad: ad, device: device, t: now
    }));
    var dom = site === 'shopify' ? '; domain=.cudis.xyz' : '';
    document.cookie = COOKIE + '=' + payload + '; path=/; max-age=' + (ATTR_DAYS * 86400) +
      dom + '; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
  } catch (e) {}

  // ── transport ───────────────────────────────────────────────────────────
  var queue = [], flushT = null;
  function base() {
    return { site: site, lp: lp, sid: sid, vid: vid, src: src, cmp: cmp, ad: ad,
             path: path, device: device };
  }
  function send(list, sync) {
    if (!list.length) return;
    var body = JSON.stringify({ batch: list });
    try {
      if (sync && navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }));
        return;
      }
      fetch(ENDPOINT, { method: 'POST', body: body, keepalive: true,
                        headers: { 'Content-Type': 'text/plain' } }).catch(function () {});
    } catch (e) {}
  }
  // Batch on a 2s debounce so a burst of clicks is one request, but flush
  // immediately for anything that might be the last thing before a navigation.
  function track(type, val, meta, immediate) {
    var e = base(); e.type = type;
    if (val != null) e.val = val;
    if (meta) e.meta = meta;
    queue.push(e);
    if (immediate || queue.length >= 20) { flush(true); return; }
    if (!flushT) flushT = setTimeout(function () { flush(false); }, 2000);
  }
  function flush(sync) {
    if (flushT) { clearTimeout(flushT); flushT = null; }
    var out = queue; queue = [];
    send(out, sync);
  }

  // ── pageview ────────────────────────────────────────────────────────────
  // Referrer rides on the view row only; repeating it on every event would be
  // the same string a hundred times.
  (function () {
    var e = base();
    e.type = 'view';
    e.ref = (document.referrer || '').slice(0, 200) || null;
    e.meta = { vh: window.innerHeight, title: (document.title || '').slice(0, 120) };
    send([e], false);
  })();

  // ── scroll depth ────────────────────────────────────────────────────────
  var marks = [25, 50, 75, 100], hit = {}, maxDepth = 0;
  function depth() {
    var h = document.documentElement.scrollHeight - window.innerHeight;
    if (h <= 0) return 100;
    return Math.max(0, Math.min(100, Math.round((window.pageYOffset / h) * 100)));
  }
  function onScroll() {
    var d = depth();
    if (d > maxDepth) maxDepth = d;
    for (var i = 0; i < marks.length; i++) {
      var m = marks[i];
      if (maxDepth >= m && !hit[m]) { hit[m] = 1; track('scroll', m); }
    }
  }
  var scrollT = null;
  addEventListener('scroll', function () {
    if (scrollT) return;
    scrollT = setTimeout(function () { scrollT = null; onScroll(); }, 250);
  }, { passive: true });
  onScroll();

  // ── clicks ──────────────────────────────────────────────────────────────
  var clicks = 0, rage = 0, lastClick = 0, lastX = 0, lastY = 0;
  function label(el) {
    var t = (el.getAttribute('aria-label') || el.getAttribute('data-track') ||
             el.textContent || el.value || '').replace(/\s+/g, ' ').trim();
    if (!t) t = el.tagName.toLowerCase() + (el.className ? '.' + String(el.className).split(' ')[0] : '');
    return t.slice(0, 80);
  }
  function section(el) {
    var n = el;
    while (n && n !== document.body) {
      if (n.tagName === 'SECTION' || n.hasAttribute('data-sec')) {
        return (n.getAttribute('data-sec') || n.className || n.id || 'section')
          .split(' ')[0].slice(0, 40);
      }
      n = n.parentElement;
    }
    return null;
  }
  addEventListener('click', function (ev) {
    var el = ev.target && ev.target.closest &&
      ev.target.closest('a,button,[role="button"],.btn,.tab,.opt,input[type="submit"],[data-track]');
    clicks++;

    // Rage = 3 clicks inside 600ms within 30px. Fires once per burst.
    var t = Date.now();
    if (t - lastClick < 600 && Math.abs(ev.clientX - lastX) < 30 && Math.abs(ev.clientY - lastY) < 30) {
      rage++;
      if (rage === 3) track('click', null, { el: el ? label(el) : 'no-target', sec: el ? section(el) : null, rage: 1 });
    } else { rage = 0; }
    lastClick = t; lastX = ev.clientX; lastY = ev.clientY;

    if (!el) return;
    var href = el.getAttribute && el.getAttribute('href');
    var meta = { el: label(el), sec: section(el), y: Math.round(ev.pageY) };
    if (href) meta.href = href.slice(0, 200);
    // A click that leaves the page must go out now — the debounce would lose it.
    var leaves = !!(href && !/^#/.test(href));
    track('click', null, meta, leaves);
  }, true);

  // ── active time ─────────────────────────────────────────────────────────
  // Counts only seconds where the tab is visible AND the user did something in
  // the last 30s. A backgrounded or abandoned tab stops accruing, which is the
  // difference between "session duration" and time actually spent reading.
  var secs = 0, lastAct = Date.now(), visible = !document.hidden;
  ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'].forEach(function (t) {
    addEventListener(t, function () { lastAct = Date.now(); }, { passive: true });
  });
  document.addEventListener('visibilitychange', function () {
    visible = !document.hidden;
    if (document.hidden) exit();
    else exited = false;   // re-arm: a tab switched away and back must still
                           // report its final duration. The sessions view takes
                           // max(val), so the extra exit rows cost nothing.
  });

  var STEPS = [10, 30, 60, 120, 240, 480, 900];
  var stepI = 0;
  setInterval(function () {
    if (!visible || (Date.now() - lastAct) > 30000) return;
    secs++;
    ls('cudis_sid_ts', String(Date.now()));
    if (stepI < STEPS.length && secs >= STEPS[stepI]) {
      stepI++;
      track('engage', secs, { reached: maxDepth, clicks: clicks });
    }
  }, 1000);

  // ── exit ────────────────────────────────────────────────────────────────
  var exited = false;
  function exit() {
    if (exited) return;
    exited = true;                          // one exit row per page life
    track('exit', secs, {
      reached: maxDepth,
      clicks: clicks,
      bounced: secs < 10 && maxDepth < 25 && clicks === 0
    }, true);
  }
  addEventListener('pagehide', exit);
  addEventListener('beforeunload', exit);
})();
